import CryptoJS from 'crypto-js'

// AES加密配置 - 与后端保持一致
const AES_CONFIG = {
  // 密钥 - 必须与后端保持一致
  KEY: 'tea_order_system_secret_key_2024',
  // 加密模式
  mode: CryptoJS.mode.ECB,
  // 填充方式
  padding: CryptoJS.pad.Pkcs7
}

/**
 * AES加密
 * @param {string|object} data - 要加密的数据，可以是字符串或对象
 * @returns {string} 加密后的字符串
 */
export function encrypt(data) {
  try {
    // 如果是对象，先转为JSON字符串
    let plainText = typeof data === 'object' ? JSON.stringify(data) : String(data)
    
    // 进行base64编码（可选，增加安全性）
    const base64Text = btoa(unescape(encodeURIComponent(plainText)))
    
    // AES加密
    const encrypted = CryptoJS.AES.encrypt(base64Text, AES_CONFIG.KEY, {
      mode: AES_CONFIG.mode,
      padding: AES_CONFIG.padding
    })
    
    return encrypted.toString()
  } catch (error) {
    console.error('AES加密失败:', error)
    throw new Error('加密失败: ' + error.message)
  }
}

/**
 * AES解密
 * @param {string} encryptedData - 加密后的字符串
 * @param {boolean} parseJson - 是否将解密结果解析为JSON对象
 * @returns {string|object} 解密后的数据
 */
export function decrypt(encryptedData, parseJson = false) {
  try {
    // 清理输入字符串中的空格和换行符
    const cleanText = encryptedData.replace(/\s+/g, '')
    
    // AES解密
    const bytes = CryptoJS.AES.decrypt(cleanText, AES_CONFIG.KEY, {
      mode: AES_CONFIG.mode,
      padding: AES_CONFIG.padding
    })
    
    // 转为UTF8字符串
    const base64Text = bytes.toString(CryptoJS.enc.Utf8)
    
    if (!base64Text) {
      throw new Error('解密失败，可能是密钥错误或数据损坏')
    }
    
    // 解码base64
    const result = decodeURIComponent(escape(atob(base64Text)))
    
    // 如果需要解析为JSON
    if (parseJson) {
      try {
        return JSON.parse(result)
      } catch (e) {
        console.warn('解密结果无法解析为JSON，返回原始字符串')
        return result
      }
    }
    
    return result
  } catch (error) {
    console.error('AES解密失败:', error)
    throw new Error('解密失败: ' + error.message)
  }
}

/**
 * 加密对象的指定字段
 * @param {object} obj - 要加密的对象
 * @param {Array<string>} fields - 需要加密的字段名数组
 * @returns {object} 加密后的对象
 */
export function encryptFields(obj, fields) {
  if (typeof obj !== 'object' || obj === null) {
    throw new Error('第一个参数必须是对象')
  }
  
  if (!Array.isArray(fields)) {
    throw new Error('第二个参数必须是字段名数组')
  }
  
  const result = { ...obj }
  
  fields.forEach(field => {
    if (Object.prototype.hasOwnProperty.call(result, field) && result[field] !== null && result[field] !== undefined) {
      result[field] = encrypt(result[field])
    }
  })
  
  return result
}

/**
 * 解密对象的指定字段
 * @param {object} obj - 要解密的对象
 * @param {Array<string>} fields - 需要解密的字段名数组
 * @param {boolean} parseJson - 是否将解密结果解析为JSON
 * @returns {object} 解密后的对象
 */
export function decryptFields(obj, fields, parseJson = false) {
  if (typeof obj !== 'object' || obj === null) {
    throw new Error('第一个参数必须是对象')
  }
  
  if (!Array.isArray(fields)) {
    throw new Error('第二个参数必须是字段名数组')
  }
  
  const result = { ...obj }
  
  fields.forEach(field => {
    if (Object.prototype.hasOwnProperty.call(result, field) && result[field] !== null && result[field] !== undefined) {
      try {
        result[field] = decrypt(result[field], parseJson)
      } catch (error) {
        console.error(`解密字段 ${field} 失败:`, error)
        result[field] = `[解密失败: ${error.message}]`
      }
    }
  })
  
  return result
}

export default {
  encrypt,
  decrypt,
  encryptFields,
  decryptFields,
  AES_CONFIG
}

